import { TextComponent } from '@manifest-cyber/design-system/lib/components/TextComponent';
import { ManifestColors } from '@manifest-cyber/design-system/lib/foundations/ts';
import { Box, Divider, Flex, Loader, Stack } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ActionableCard } from '../../../components/ActionableCard/ActionableCard';
import Icon from '../../../components/Icon/Icon';
import ManifestBadge from '../../../components/ManifestBadge/ManifestBadge';
import { ManifestCheckbox } from '../../../components/ManifestCheckbox/ManifestCheckbox';
import { ManifestTabs } from '../../../components/ManifestTabs/ManifestTabs';
import { noopFunction } from '../../../lib/noop/noopFunction';
import { useFetchRoleDetail } from './api/useFetchRoleDetail';
import { useFetchRoleUsers } from './api/useFetchRoleUsers';
import styles from './RoleDetail.module.scss';

export const RoleDetail = () => {
  const { t } = useTranslation();
  const { roleUrn } = useParams();
  const {
    data: role,
    isLoading,
    error,
    refetch,
  } = useFetchRoleDetail({
    roleId: roleUrn,
    t,
  });
  const [activeTab, setActiveTab] = useState<'permissions' | 'users'>('permissions');
  const {
    data: users,
    isLoading: isLoadingUsers,
    error: usersError,
    refetch: refetchUsers,
  } = useFetchRoleUsers({
    roleId: roleUrn,
    enabled: activeTab === 'users',
  });

  if (isLoading && !role) {
    return (
      <Flex justify="center" w="100%" pt="48px">
        <Loader
          aria-label="Loading..."
          data-testid="role-detail-loader"
          data-role={role ? 'true' : 'false'}
        />
      </Flex>
    );
  }

  if (error || !role) {
    return (
      <Flex justify="center" w="100%">
        <ActionableCard
          title={t('error.general.errorLoadingData')}
          subtitle={t('error.general.errorLoadingDataSubtitle')}
          Icon={<Icon icon="circle-exclamation" size="lg" />}
          primaryAction={{
            label: t('global.retry'),
            onClick: () => {
              refetch();
            },
            isLoading,
          }}
        />
      </Flex>
    );
  }

  return (
    <Stack spacing="lg" className={styles.container}>
      <Flex justify={'space-between'}>
        <Flex direction={'column'}>
          <TextComponent variant="headerXLarge">{role.name}</TextComponent>
        </Flex>
      </Flex>

      <ManifestTabs
        keepMounted={false}
        value={activeTab}
        onTabChange={(value: 'permissions' | 'users') => setActiveTab(value)}
      >
        <ManifestTabs.List>
          <ManifestTabs.Tab value="permissions">
            {t('page.userSettings.roles.detailTabs.general')}
          </ManifestTabs.Tab>
          <ManifestTabs.Tab value="users">
            <Flex gap="8px">
              {t('page.userSettings.roles.detailTabs.members')}

              <ManifestBadge
                children={role.usersQuantity}
                active={activeTab === 'users'}
              />
            </Flex>
          </ManifestTabs.Tab>
        </ManifestTabs.List>
        <ManifestTabs.Panel value="permissions">
          {role.permissionsGroups.map((group) => (
            <Box key={group.title} mt="32px">
              <TextComponent
                variant={group.isMainGroup ? 'headerMedium' : 'headerAllCaps'}
                color={group.isMainGroup ? 'white' : 'title'}
              >
                {group.title}
              </TextComponent>
              <Divider my="16px" color={ManifestColors.colorBorderCards} />
              {group.compositePermissions.map((permission, index) => (
                <Stack spacing="xs" mt="sm" ml="32px" key={permission.urn}>
                  <Stack>
                    <Flex>
                      <ManifestCheckbox
                        checked={permission.isSelected}
                        mr="8px"
                        disabled
                        onChange={noopFunction}
                      />
                      <TextComponent variant="bodyMedium">
                        {permission.displayName}
                      </TextComponent>
                    </Flex>
                    <TextComponent variant="bodySmall" color="muted">
                      {permission.description}
                    </TextComponent>
                  </Stack>
                  {index < group.compositePermissions.length - 1 && (
                    <Divider my="16px" color={ManifestColors.colorBorderCards} />
                  )}
                </Stack>
              ))}
            </Box>
          ))}
        </ManifestTabs.Panel>
        <ManifestTabs.Panel value="users">
          <Stack mt="32px" spacing="24px">
            {!users && isLoadingUsers && (
              <Flex justify="center" w="100%" pt="48px">
                <Loader aria-label="Loading..." data-testid="role-users-loader" />
              </Flex>
            )}

            {users && users.length === 0 && (
              <Flex justify="center" w="100%">
                <ActionableCard
                  hasIconBackground={false}
                  title={t('page.userSettings.roles.noMembers.title')}
                  Icon={<Icon icon="user" size="lg" />}
                />
              </Flex>
            )}
            {Boolean(usersError) && !users && (
              <Flex justify="center" w="100%">
                <ActionableCard
                  title={t('error.general.errorLoadingData')}
                  subtitle={t('error.general.errorLoadingDataSubtitle')}
                  Icon={<Icon icon="circle-exclamation" size="lg" />}
                  primaryAction={{
                    label: t('global.retry'),
                    onClick: () => {
                      refetchUsers();
                    },
                    isLoading: isLoadingUsers,
                  }}
                />
              </Flex>
            )}
            {users?.map((user) => (
              <Flex key={user.id} justify={'space-between'}>
                <Flex gap="10px">
                  <TextComponent variant="bodyLarge" color="primary">
                    <Icon icon="user" />
                  </TextComponent>
                  <Flex direction="column" gap="2px">
                    <TextComponent variant="bodyMedium">{user.email}</TextComponent>
                    {user.fullName && (
                      <TextComponent variant="bodySmall" color="muted">
                        {user.fullName}
                      </TextComponent>
                    )}
                  </Flex>
                </Flex>
              </Flex>
            ))}
          </Stack>
        </ManifestTabs.Panel>
      </ManifestTabs>
    </Stack>
  );
};
